import { StylesConfig, GroupBase, Theme } from 'react-select';
import { ReactSelectType } from '../types';
import { ColorMode } from '@chakra-ui/react';

export type SelectBorderStyle = 'solid' | 'dotted' | 'dashed' | 'none';

export interface SelectStyle {
  borderStyle?: SelectBorderStyle;
  borderRadius?: number | string;
  isAngularTheme?: boolean;
  minHeight?: number | string;
  mode: ColorMode;
  variant?: 'entity-directory';
}

const gray50 = '#ededed';
const gray100 = '#d9d9d9';
const gray200 = '#bdbdbd';
const gray300 = '#999999';
const gray400 = '#787878';
const gray500 = '#5c5c5c';
const gray600 = '#474747';

const blackAlpha = {
  200: 'RGBA(0, 0, 0, 0.08)',
  300: 'RGBA(0, 0, 0, 0.16)',
  400: 'RGBA(0, 0, 0, 0.24)',
  500: 'RGBA(0, 0, 0, 0.36)',
};

const whiteAlpha = {
  300: 'RGBA(255, 255, 255, 0.16)',
};

const sandColors = {
  10: '#F8E2C8',
  20: '#F3DABE',
  30: '#E8CBAB',
  50: '#DEBE9A',
  100: '#C6A37C',
  40085: '#876d50D9',
};

export function reactSelectStyle<IsMulti extends boolean, T = ReactSelectType>(
  selectStyle: SelectStyle = {
    borderStyle: 'solid',
    isAngularTheme: false,
    mode: 'dark',
  },
): StylesConfig<T, IsMulti, GroupBase<T>> | undefined {
  const isDark = selectStyle.mode === 'dark';
  const isEntityDirectory = selectStyle.variant === 'entity-directory';
  return {
    indicatorSeparator: (base, _) => ({
      ...base,
      display: isEntityDirectory ? 'none' : base.display,
      backgroundColor: selectStyle.isAngularTheme
        ? 'gray'
        : isDark
        ? gray500
        : gray200,
    }),
    dropdownIndicator: (base, _) => ({
      ...base,
      padding: isEntityDirectory ? '6px' : base.padding,
      color: selectStyle.isAngularTheme ? 'gray' : isDark ? gray500 : gray200,
    }),
    option: (base, _) => ({
      ...base,
      color: 'black',
      fontFamily: isEntityDirectory ? 'Instrument Sans' : base.fontFamily,
      fontSize: '14px',
    }),
    control: (base, _) => ({
      ...base,
      backgroundColor: 'transparent',
      borderRadius: isEntityDirectory
        ? 0
        : selectStyle.borderRadius ?? base.borderRadius,
      borderColor: selectStyle.isAngularTheme
        ? 'gray'
        : isEntityDirectory
        ? 'transparent'
        : isDark
        ? gray500
        : gray200,
      ':hover': {
        ...base[':hover'],
        backgroundColor: isEntityDirectory
          ? isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : '#f0eee6'
          : base[':hover']?.backgroundColor,
        borderColor: selectStyle.isAngularTheme
          ? gray200
          : isEntityDirectory
          ? 'transparent'
          : isDark
          ? gray400
          : gray300,
        borderStyle: isEntityDirectory ? 'none' : selectStyle.borderStyle,
      },
      ':focus-within': {
        ...base[':focus-within'],
        backgroundColor: isEntityDirectory
          ? isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : '#f0eee6'
          : base[':focus-within']?.backgroundColor,
        borderColor: selectStyle.isAngularTheme
          ? gray200
          : isEntityDirectory
          ? 'transparent'
          : isDark
          ? gray400
          : gray300,
        borderStyle: isEntityDirectory ? 'none' : selectStyle.borderStyle,
      },
      boxShadow: 'none',
      borderStyle: isEntityDirectory ? 'none' : selectStyle.borderStyle,
      fontFamily: isEntityDirectory ? 'Instrument Sans' : base.fontFamily,
      minHeight: selectStyle.minHeight ?? (isEntityDirectory ? '38px' : '46px'),
      minWidth: 0,
      overflow: 'hidden',
    }),
    valueContainer: (base, _) => ({
      ...base,
      padding: isEntityDirectory ? '0 8px' : base.padding,
      flex: '1 1 0',
      minWidth: 0,
      overflow: 'hidden',
    }),
    indicatorsContainer: (base, _) => ({
      ...base,
      flexShrink: 0,
    }),
    input: (base, _) => ({
      ...base,
      color: selectStyle.isAngularTheme ? gray600 : isDark ? 'white' : 'black',
      visibility: 'inherit', // Fix to prevent input bleed out when popover is hidden
    }),
    singleValue: (base, _) => ({
      ...base,
      color: selectStyle.isAngularTheme ? gray600 : isDark ? 'white' : 'black',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    container: (base, _) => ({
      ...base,
      width: '100%',
    }),
    multiValue: (base, _) => ({
      ...base,
      backgroundColor: isDark ? whiteAlpha[300] : blackAlpha[300],
    }),
    multiValueLabel: (base, _) => ({
      ...base,
      color: isDark ? 'white' : 'black',
    }),
  };
}

export const reactSelectTheme = (
  theme: Theme,
  colorMode: ColorMode = 'dark',
) => {
  const isDark = colorMode === 'dark';
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: isDark ? sandColors[50] : gray100,
      primary25: isDark ? sandColors[10] : gray50,
      primary50: isDark ? sandColors[30] : gray50,
    },
  };
};

export function navLinkOffsetStyle(
  isActive: boolean,
  scale: number,
  colorMode: ColorMode = 'dark',
) {
  const isDark = colorMode === 'dark';
  return {
    transition:
      'outline-offset 200ms ease, outline-color 200ms ease, transform 300ms ease',
    outline: '2px solid',
    outlineOffset: 12,
    outlineColor: 'transparent',
    ...(isActive
      ? {
          outlineOffset: 4,
          outlineColor: isDark ? 'white' : 'black',
          transform: `scale(${scale})`,
        }
      : null),
  };
}
